/**
 * Topic Detail View
 *
 * Renders the per-topic detail view shown in the right panel when a user
 * clicks a topic row. Layout, top → bottom:
 *   1. Header: title + the topic's registry description as its subtitle.
 *   2. The understanding box — one card carrying both how much is understood
 *      and what is understood. Its head is the KPI block (eyebrow, then the
 *      depth stepper beside the band word and section count — the band type is
 *      shared with the Progress hero so the two cannot drift) and the lede
 *      across a rule. Measure and meaning belong in one container rather than
 *      a strip above it restating what the card already is, which is also why
 *      the card carries no `sd-takeaway__label`: the KPI eyebrow names it.
 *      The card's body is cold (subcategory themes) or warm (the topic-summary
 *      portrait), hydrated asynchronously from GET /api/topics/:id/summary and
 *      showing a skeleton until then. Each state renders as `{ lede, body }`:
 *      only those two slots swap, so the head — and the mounted chart in it —
 *      survives hydration.
 *   3. Threads zone — up to 3 topic-scoped open threads under a collapsible
 *      disclosure header (open by default); each card drills into the shared
 *      thread detail view (in-panel, with a back button). Hidden until hydrated,
 *      and stays hidden when there are none.
 *   4. Sections — the shared section checklist (components/sectionChecklist.js,
 *      also the path drawer's) under a collapsible disclosure header (open by
 *      default).
 *
 * The view is pure rendering — state lives in DiscoverySection. The
 * `renderUnderstanding*` / `renderTopicThreads` exports are the hydration
 * fragments DiscoverySection injects once the summary fetch resolves.
 */

import { levelLabel, renderDepthStepper } from './depthProgress.js';
import { topicFill } from './topicPalette.js';
import { renderSectionChecklist, statusForTier } from './sectionChecklist.js';
import { renderDisclosure, escapeHtml } from './disclosure.js';
import { renderThreadNavCard } from './threadDrillHelper.js';

/**
 * Render the full topic detail view.
 *
 * @param {Object} topic - Topic data (from DiscoverySection.progressTopics).
 * @returns {string} HTML string to inject into the slide-panel detail view.
 */
export function renderTopicDetailView(topic) {
  const topicProgress = topic.progress ?? 0;
  const description = (topic.description || '').trim();

  const sections = topic.sections || [];
  // Default marker palette, same as the path drawer's — state reads as state on
  // both surfaces, and topic identity is already carried by the hue dot in the
  // title. `statusForTier` keeps the marker set consistent with the
  // `sectionsExplored` count below.
  // Completed sections surface first (then in-progress, then not-started) so
  // the list leads with what's resolved instead of burying it in authored
  // order; a stable sort keeps each group's original relative order. Unlike
  // the path drawer's checklist — same component, but authored curriculum
  // order there is the point — this list has no sequence to preserve.
  const STATUS_RANK = { complete: 0, in_progress: 1, not_started: 2 };
  const sectionsHtml = renderSectionChecklist(
    sections
      .map(section => ({
        key: section.code,
        name: section.name,
        description: section.description,
        status: statusForTier(section.tier)
      }))
      .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
  );

  // "Explored" = section has a tier beyond unexplored/declined. Of the three
  // terminal tiers only `declined` is excluded: `l2_withheld` and `l2_skipped`
  // both sit on a completed questionnaire, so they count as explored.
  const sectionsExplored = sections.filter(
    s => s.tier && s.tier !== 'none' && s.tier !== 'declined'
  ).length;
  const sectionCountTotal = topic.sectionCount ?? sections.length;

  // Title + description + overview (depth ring + band/section stack) render
  // in the body; the "Explore this topic" CTA is the panel's header primary
  // button (wired in DiscoverySection). Understanding/threads/sections below
  // use the shared sd-* detail vocabulary.
  return `
    <div class="topic-detail content-width" data-topic-id="${escapeHtml(topic.id)}">
      <header class="topic-detail__header">
        <h1 class="topic-detail__title">
          <span class="topic-detail__dot" aria-hidden="true" style="background:${topicFill(topic.id)}"></span>${escapeHtml(topic.label)}
        </h1>
        ${description ? `<p class="sd-subtitle topic-detail__description">${escapeHtml(description)}</p>` : ''}
      </header>

      <section class="topic-detail__box sd-takeaway sd-takeaway--plate">
        <div class="topic-detail__kpi">
          <span class="topic-detail__kpi-eyebrow">Topic understanding</span>
          <div class="topic-detail__kpi-row">
            <div class="topic-detail__kpi-state">
              ${renderDepthStepper(topicProgress, { color: topicFill(topic.id) })}
              <div class="topic-detail__kpi-text">
                <span class="topic-detail__kpi-band">${escapeHtml(levelLabel(topicProgress))}</span>
                ${renderSectionCount(sectionsExplored, sectionCountTotal, sections.length > 0)}
              </div>
            </div>
            <div class="topic-detail__lede" data-understanding-lede></div>
          </div>
        </div>
        <div class="topic-detail__understanding" data-understanding>
          ${renderUnderstandingSkeleton()}
        </div>
      </section>

      <div class="topic-detail__threads sd-zone-wrap" data-threads hidden></div>

      <section class="topic-detail__sections sd-zone-wrap" data-sections-anchor tabindex="-1">
        ${renderDisclosure('topic-sections', 'Sections in this topic', sectionsHtml, { expanded: true, count: sections.length })}
      </section>
    </div>
  `;
}

/**
 * The explored-sections count. A jump to the checklist it is counting whenever
 * there IS one — the portrait and threads between them put that list well below
 * the fold on a long topic. A button, not an anchor: the panel owns its own URL
 * (`?topic=N`) and a real `href="#…"` would push a competing hash into history.
 *
 * @param {number} explored - Sections carrying any depth
 * @param {number} total - Sections in the topic
 * @param {boolean} jumpable - Whether a checklist exists to scroll to
 * @returns {string} HTML fragment
 */
function renderSectionCount(explored, total, jumpable) {
  const label = `${explored} of ${total} sections explored`;
  if (!jumpable) {
    return `<span class="sd-plate-jump-row">${escapeHtml(label)}</span>`;
  }
  return `
    <button type="button" class="sd-plate-jump-row sd-plate-jump"
            data-scroll-to-sections aria-label="${escapeHtml(label)} — jump to the section list">
      <span>${escapeHtml(label)}</span>
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  `;
}

/** Loading placeholder for the understanding zone while the summary fetches. */
function renderUnderstandingSkeleton() {
  return `
    <div class="topic-detail__skeleton" aria-hidden="true">
      <span class="topic-detail__skeleton-line"></span>
      <span class="topic-detail__skeleton-line"></span>
      <span class="topic-detail__skeleton-line topic-detail__skeleton-line--short"></span>
    </div>
  `;
}

/**
 * Cold-state understanding: an invitation, then the topic's subcategory themes
 * (static taxonomy content from the summary endpoint's `themes`).
 *
 * @param {Array<{ name: string }>} [themes=[]]
 * @returns {{lede: string, body: string}} The card's two swappable slots
 */
export function renderUnderstandingCold(themes = []) {
  const themesHtml = (themes || [])
    .filter(t => t && t.name)
    .map(t => `
      <li class="topic-detail__theme">
        <span class="topic-detail__theme-dot" aria-hidden="true"></span>
        <span class="topic-detail__theme-name">${escapeHtml(t.name)}</span>
      </li>
    `).join('');
  return {
    lede: `<p class="topic-detail__invite">We haven’t explored this together yet${themesHtml ? ' — here’s what we’d look at' : ''}.</p>`,
    body: themesHtml ? `<ul class="topic-detail__themes">${themesHtml}</ul>` : ''
  };
}

/**
 * Warm-state understanding: the topic-summary portrait. The one-line card
 * blurb becomes the card head's lede, beside the KPI; the summary prose splits
 * into the body's paragraphs.
 *
 * @param {{ cardBlurb?: string|null, summary?: string|null }} data
 * @returns {{lede: string, body: string}} The card's two swappable slots
 */
export function renderUnderstandingWarm(data = {}) {
  const paragraphs = String(data.summary || '')
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => `<p class="sd-summary">${escapeHtml(p).replace(/\n/g, ' ')}</p>`)
    .join('');
  return {
    lede: data.cardBlurb && data.cardBlurb.trim()
      ? `<p class="sd-plate-lede">${escapeHtml(data.cardBlurb.trim())}</p>`
      : '',
    body: `<div class="topic-detail__portrait">${paragraphs}</div>`
  };
}

/**
 * Threads zone: up to a few topic-scoped open threads, rendered with the
 * shared thread-card visual (`renderThreadNavCard`, threadDrillHelper.js —
 * also used by session detail's "Threads to pick up" section). Each card
 * carries `data-thread-id`; DiscoverySection delegates clicks to drill into
 * the thread-detail panel.
 *
 * @param {Array<{ id: string|number, headline?: string, question?: string, context?: string }>} threads
 * @returns {string} HTML fragment ('' when there are no threads).
 */
export function renderTopicThreads(threads = []) {
  if (!threads || !threads.length) return '';
  const cards = threads.map(renderThreadNavCard).join('');
  return renderDisclosure('topic-threads', 'Threads to pick up',
    `<div class="topic-detail__thread-list">${cards}</div>`,
    { expanded: true, count: threads.length });
}
